import { useState, type ReactNode } from 'react'
import { ScrollView, View } from 'react-native'
import { MODAL_BOTTOM_SCREEN_GAP } from '@ca/ui/components/Modal/variants/ModalBottom'
import ChipButton from '@ca/ui/elements/ChipButton'
import Messages from '@ca/ui/features/measurement/Messages'
import Oval from '@ca/ui/features/measurement/Oval'
import type { MeasurementOvalNormalizedFrame } from '@ca/ui/features/measurement/Oval'
import {
  MESSAGES_CONTAINER_HEIGHT,
  MESSAGES_CONTAINER_PADDING_HORIZONTAL,
  MESSAGES_CONTAINER_PADDING_VERTICAL,
} from '@ca/ui/features/measurement/Messages/styles'
import { useCompletedFadeOut } from './internal/useCompletedFadeOut'
import { useHoldStillTransition } from './internal/useHoldStillTransition'
import {
  OVAL_SCALE_DEFAULT,
  OvalScaleDebugControl,
} from './internal/OvalScaleDebugControl'
import type { MeasurementUiMessage, MeasurementUiOvalState } from './types'

type Props = {
  message: MeasurementUiMessage | null
  progress: number
  camera: ReactNode
  pointOverlay?: ReactNode
  ovalState: MeasurementUiOvalState
  ovalFrame?: MeasurementOvalNormalizedFrame
  isOvalVisible?: boolean
  isInitializing?: boolean
  isCloseVisible?: boolean
  closeLabel: string
  isCloseDisabled?: boolean
  onClose: () => void
  isInstructionsVisible?: boolean
  instructionsLabel: string
  onInstructions: () => void
  /** Extra chips rendered left of the instructions chip (e.g. the vbox chip). */
  options?: ReactNode
  /** Extra chips rendered immediately left of Close (e.g. dev settings). */
  closeActions?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  /** Absolutely-positioned content rendered above everything (debug controls). */
  overlay?: ReactNode
  /**
   * Debug-build flag. Shows the oval-scale overlay so we can simulate a
   * zoomed-out camera without an iPad 8.
   */
  isDebug?: boolean
}

export function MeasurementFlowScaffold({
  message,
  progress,
  camera,
  pointOverlay,
  ovalState,
  ovalFrame,
  isOvalVisible = true,
  isInitializing = false,
  isCloseVisible = true,
  closeLabel,
  isCloseDisabled,
  onClose,
  isInstructionsVisible = true,
  instructionsLabel,
  onInstructions,
  options,
  closeActions,
  children,
  footer,
  overlay,
  isDebug = false,
}: Props) {
  const [ovalScale, setOvalScale] = useState(OVAL_SCALE_DEFAULT)
  const presentationMessage = isInitializing ? 'initial' : message
  const presentationProgress = isInitializing ? 0 : progress
  const presentationOvalState = isInitializing ? 'initial' : ovalState
  const { message: displayMessage, ovalState: displayOvalState } =
    useHoldStillTransition(presentationMessage, presentationOvalState)
  const isMessagesOpen = useCompletedFadeOut(displayMessage)
  const hasLeftActions = Boolean(options || isInstructionsVisible)
  const hasRightActions = Boolean(closeActions || isCloseVisible)

  return (
    <View className="flex-1 bg-black">
      <View className="absolute inset-0 overflow-hidden bg-black">
        {camera}
        {pointOverlay}
        <Oval
          state={displayOvalState}
          normalizedFrame={ovalFrame}
          scale={ovalScale}
          isVisible={isOvalVisible}
        />
        {/* Positioned to match the Figma "Prompt Component": a full-width
            strip at the top of the screen whose padding centers the inner
            message box (same wrapper as the dev measurements screen). */}
        <View className="absolute left-0 right-0 top-0" pointerEvents="none">
          <Messages
            isOpen={isMessagesOpen}
            message={displayMessage}
            progress={presentationProgress}
            ovalFrame={ovalFrame}
            ovalScale={ovalScale}
          />
        </View>
        {hasLeftActions ? (
          <MeasurementActions>
            <View className="flex-row items-center gap-4">
              {options}
              {isInstructionsVisible ? (
                <ChipButton
                  value={instructionsLabel}
                  onPress={onInstructions}
                />
              ) : null}
            </View>
            {hasRightActions ? (
              <View className="flex-row items-center gap-4">
                {closeActions}
                {isCloseVisible ? (
                  <ChipButton
                    value={closeLabel}
                    disabled={isCloseDisabled}
                    onPress={onClose}
                  />
                ) : null}
              </View>
            ) : null}
          </MeasurementActions>
        ) : null}
        {hasRightActions && !hasLeftActions ? (
          <MeasurementCloseButton
            label={closeLabel}
            disabled={isCloseDisabled}
            onPress={onClose}
            before={closeActions}
            isCloseVisible={isCloseVisible}
          />
        ) : null}
      </View>

      {(children || footer) && (
        <View
          className="absolute bottom-0 left-0 right-0 bg-white p-8"
          style={{ maxHeight: '46%', minHeight: 112 }}
        >
          {children ? (
            <ScrollView
              className="mb-4"
              contentContainerClassName="gap-4"
              showsVerticalScrollIndicator={false}
            >
              {children}
            </ScrollView>
          ) : null}
          {footer && (
            <View
              className="flex-row gap-3"
              style={{ height: 48, minHeight: 48 }}
            >
              {footer}
            </View>
          )}
        </View>
      )}

      {isDebug ? (
        <OvalScaleDebugControl
          scale={ovalScale}
          ovalFrame={ovalFrame}
          onChange={setOvalScale}
        />
      ) : null}
      {overlay}
    </View>
  )
}

type MeasurementCloseButtonProps = {
  label: string
  disabled?: boolean
  onPress: () => void
  before?: ReactNode
  isCloseVisible?: boolean
}

export function MeasurementCloseButton({
  label,
  disabled,
  onPress,
  before,
  isCloseVisible = true,
}: MeasurementCloseButtonProps) {
  return (
    <View
      className="absolute flex-row items-center gap-4"
      style={{
        right: MODAL_BOTTOM_SCREEN_GAP,
        top: MESSAGES_CONTAINER_PADDING_VERTICAL,
      }}
    >
      {before}
      {isCloseVisible ? (
        <ChipButton value={label} disabled={disabled} onPress={onPress} />
      ) : null}
    </View>
  )
}

/**
 * Bottom action row from Figma: options on the left and close on the right,
 * inset by the same screen gap.
 */
function MeasurementActions({ children }: { children?: ReactNode }) {
  return (
    <View
      className="absolute flex-row items-center justify-between"
      pointerEvents="box-none"
      style={{
        left: MODAL_BOTTOM_SCREEN_GAP,
        right: MODAL_BOTTOM_SCREEN_GAP,
        bottom: MODAL_BOTTOM_SCREEN_GAP,
      }}
    >
      {children}
    </View>
  )
}
